import type { ReactNode } from 'react';

import { Link, Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';
import { OpenInNewIcon } from '@change/ds-icons';

import { getHelpUrl } from '@change-corgi/config/help';
import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { createAnchorToLinkReplacer } from '#src/shared/ds';

import { useLocale } from '#src/app/shared/hooks/l10n';

/** The "Learn more" explainer for the journalist ask: how journalists are vetted, and how to opt out. */
export const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ triggerLabel, onTriggerClick }) => {
	const { translate } = useI18n();
	const locale = useLocale();
	const helpUrl = getHelpUrl(locale, 'contact-us');

	return (
		<Modal
			trigger={
				<Link as="button" variant="inherit" size="inherit" onClick={onTriggerClick} data-qa="learn-more-button">
					{triggerLabel ?? <TranslateHtml value="corgi.petition.starter_onboarding.phone_collection_step.learn" />}
				</Link>
			}
			onClose={() => {}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader
				title={translate('corgi.petition.starter_onboarding.phone_collection_step.learn_more_modal.title')}
			/>
			<ModalBody>
				<ul className="grid list-disc gap-6 pl-4">
					<li>
						<Text>
							<Translate value="corgi.petition.starter_onboarding.phone_collection_step.learn_more_modal.item_1" />
						</Text>
					</li>
					<li>
						<Text>
							<Translate value="corgi.petition.starter_onboarding.phone_collection_step.learn_more_modal.item_2" />
						</Text>
					</li>
					<li>
						<Text>
							<TranslateHtml
								value="corgi.petition.starter_onboarding.phone_collection_step.learn_more_modal.item_3"
								replacements={{ helpUrl }}
								replace={createAnchorToLinkReplacer({ trailingIcon: <OpenInNewIcon /> })}
								allowLinkTargetAttr
							/>
						</Text>
					</li>
				</ul>
			</ModalBody>
		</Modal>
	);
};

type LearnMoreModalProps = Readonly<{
	// A node rather than a translation key, because @change-corgi/no-invalid-translation-keys
	// requires every Translate/TranslateHtml call site to pass a string literal.
	triggerLabel?: ReactNode;
	/** Injected so each flow names its own open event. */
	onTriggerClick: () => void;
}>;
